import React, { useEffect, useMemo } from 'react';
import { Box } from '@mui/material';
import { matchPath, useLocation } from 'react-router-dom';
import { useIsMobile } from '../../../hooks/useIsMobile';

const routeComponentMap: [string, string][] = [
  ['/', 'MobileHome.tsx'],
  ['/home', 'MobileHome.tsx'],
  ['/about', 'MobileAboutUs.tsx'],
  ['/magazines', 'MobileMagazines.tsx'],
  ['/magazines/vivekvani', 'MobileBooksRoute.tsx'],
  ['/magazines/kalyan/category/:catid/:kalyanid', 'mMagazineHomePage.tsx'],
  ['/magazines/kalyan/category/:catid', 'mMagazineListPage.tsx'],
  ['/magazines/kalyan/:slug', 'mMagazineHomePage.tsx'],
  ['/magazines/kalyan', 'MobileKalyansPage.tsx'],
  ['/magazines/kalyanakalpataru/category/:catid/:kalyanid', 'mMagazineHomePage.tsx'],
  ['/magazines/kalyanakalpataru/category/:catid', 'mMagazineListPage.tsx'],
  ['/magazines/kalyanakalpataru/:slug', 'mMagazineHomePage.tsx'],
  ['/magazines/kalyanakalpataru', 'MobileKalyansPage.tsx'],
  ['/magazines/geetgovind/category/:catid', 'MBookDetailPage.tsx'],
  ['/magazines/geetgovind/special', 'MBookDetailPage.tsx'],
  ['/magazines/geetgovind/author/:id', 'MBookDetailPage.tsx'],
  ['/magazines/geetgovind/language/:langid', 'MBookDetailPage.tsx'],
  ['/magazines/geetgovind/:slug', 'MBookDetailPage.tsx'],
  ['/magazines/geetgovind/special/:id', 'MBookDetailPage.tsx'],
  ['/magazines/geetgovind/category/:catid/:id', 'MBookDetailPage.tsx'],
  ['/magazines/geetgovind/language/:langid/:id', 'MBookDetailPage.tsx'],
  ['/magazines/geetgovind', 'mMagazineHomePage.tsx'],
  ['/books', 'MobileBooksRoute.tsx'],
  ['/books/special', 'MobileBooksRoute.tsx'],
  ['/books/author/:id', 'MobileBooksRoute.tsx'],
  ['/books/language/:langid', 'MobileBooksRoute.tsx'],
  ['/books/category/:catid', 'MobileBooksRoute.tsx'],
  ['/books/:bookid', 'mBookDetailPage.tsx'],
  ['/reader/:type/:id', 'MobileEpub.tsx'],
  ['/pravachans', 'MobilePravachans.tsx / MobileAudioListPage.tsx'],
  ['/pravachans/special', 'MobilePravachans.tsx'],
  ['/pravachans/author/:id', 'MobilePravachans.tsx'],
  ['/pravachans/:id', 'AudiosDetailPage.tsx'],
  ['/audios', 'MbAudioCategory.tsx / MobileAudioListPage.tsx'],
  ['/audios/special', 'MobileAudioListPage.tsx'],
  ['/audios/author/:id', 'MobileAudioListPage.tsx'],
  ['/audios/:slug', 'MbAudioPlayerPage.tsx'],
  ['/DailySatsang', 'DailySatsangPage.tsx / MobileAudioListPage.tsx'],
  ['/DailySatsang/:slug', 'AudiosDetailPage.tsx'],
  ['/videos', 'MbAllVideos.tsx'],
  ['/videos/author/:id', 'MbAllVideos.tsx'],
  ['/videos/category/:id', 'MbAllVideos.tsx'],
  ['/videos/video/:id', 'MbAllVideos.tsx'],
  ['/articles', 'MobileArticlesPage.tsx'],
  ['/articles/special', 'MobileArticlesPage.tsx'],
  ['/articles/author/:id', 'MobileArticlesPage.tsx'],
  ['/divinequotes', 'MobileDivineQuotes.tsx'],
  ['/privacypolicy', 'MobilePrivacyPolicy.tsx'],
  ['/termsofuse', 'MobileTermsOfUse.tsx'],
  ['/filter', 'MobileBooksFilterPage.tsx'],
  ['/myFavourite', 'MobileFavoriatePage.tsx'],
  ['/myProfile', 'MobileProfilePage.tsx'],
  ['/divineQuotes', 'MobileDivineQuotes.tsx'],
  ['/divineQuotesImg', 'DivineQuotesImageview.tsx'],
  ['/subjectData', 'FilteredSubjectData.tsx'],
  ['/recentlyViewed', 'MobileRecentlyViewed.tsx'],
  ['/readArticles', 'ReadArticles.tsx'],
  ['/loginPage', 'MobileLoginPage.tsx'],
  ['/mobileAudioListPage', 'MobileAudioListPage.tsx'],
  ['/categoriesFilter', 'CategoriesFilter.tsx'],
  ['/audioPlayerPage', 'MbAudioPlayerPage.tsx'],
  ['/mbAudioCategory', 'MbAudioCategory.tsx'],
  ['/mobileArticles', 'MobileArticlesPage.tsx'],
  ['/terms-of-use', 'MobileTermsOfUse.tsx'],
  ['/privacy-policy', 'MobilePrivacyPolicy.tsx'],
  ['/publicity', 'MobilePromotion.tsx'],
  ['/pamphletEng', 'MobileEnglishTemplate.tsx'],
  ['/pamphletHindi', 'MobileHindiTemplate.tsx'],
  ['/legendsIntroduction', 'LegendsIntroduction.tsx'],
  ['/eBooks', 'mBookHoriCatViewPage.tsx'],
  ['/profile/', 'MobileProfilePage.tsx'],
  ['/profile/My-Favourites', 'MobileFavoriatePage.tsx'],
  ['/Login/*', 'MobileLoginPage.tsx'],
  ['/webView', 'WebViewforbook.tsx'],
  ['/fullimage', 'FullImage.tsx'],
];

function getPageFile(pathname: string): string {
  for (const [pattern, component] of routeComponentMap) {
    if (matchPath(pattern, pathname)) {
      return component;
    }
  }
  return '—';
}

/**
 * Development-only fixed bar showing the current mobile route and component file name.
 * Hidden on desktop and stripped from production builds via NODE_ENV.
 */
const MobileDevPageInfoBar: React.FC = () => {
    const location = useLocation();
    const isMobile = useIsMobile();
    const isDev = process.env.NODE_ENV === 'development';
    const pathLabel = `${location.pathname}${location.search || ''}`;
    const shouldShow = isDev && isMobile;
    const pageFile = useMemo(() => getPageFile(location.pathname), [location.pathname]);

    useEffect(() => {
        if (!shouldShow) return;
        const logdetails: string[] = [];
        logdetails.push('dev_page_info_bar_visible');
        logdetails.push(`path=${pathLabel}`);
        logdetails.push(`component=${pageFile}`);
        console.log('[MobileDevPageInfoBar]', logdetails.join(' | '));
    }, [shouldShow, pathLabel, pageFile]);

    if (!shouldShow) {
        return null;
    }

    return (
        <Box
            sx={{
                width: '100%',
                minHeight: '40px',
                backgroundColor: '#f97316',
                color: '#000000',
                position: 'fixed',
                bottom: 0,
                left: 0,
                zIndex: 9999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 1,
                py: 0.5,
                boxSizing: 'border-box',
                pointerEvents: 'none',
            }}
            aria-hidden="true"
        >
            <Box
                component="span"
                sx={{
                    fontSize: '13px',
                    fontWeight: 600,
                    fontFamily: 'chanakyaUni, sans-serif',
                    textAlign: 'center',
                    wordBreak: 'break-all',
                }}
            >
                {pageFile} | {pathLabel}
            </Box>
        </Box>
    );
};

export default MobileDevPageInfoBar;
